import { FileSearch, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { CardSkeleton } from "@/components/Loading";
import type { IpaDetailResponse, IpaLineItem, IpaListResponse, IpaStockCoverage, Role } from "../../shared/api";
import { PERMISSIONS } from "../../shared/permissions";
import { daysLeftText, formatDate, formatKg, formatNumber, formatPct, formatUsd } from "../../shared/versions";
import { ErrorAlert } from "@/components/ErrorAlert";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { BarList, Meter } from "@/components/charts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { pathOf, screens } from "../screens";
import { navigate, replaceQuery } from "@/lib/nav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorText, getIpaDetail, getIpaList, getIpaXlsx } from "@/lib/client";

const TITLE = { imports: "İthalat taahhüdü", exports: "İhracat taahhüdü" } as const;

const TH = "bg-muted/60 text-xs font-medium text-muted-foreground first:pl-4 last:pr-4";
const TD = "first:pl-4 last:pr-4";

/** Taahhüt satırı: gerçekleşme çubuğu (sayılar API'den; çubuk yalnız görsel), kalan, süre sonu, öngörü, ithal girdi karşılığı. */
export function LineCard({ line }: { line: IpaLineItem }) {
  const t = TITLE[line.commitmentType];
  const pct = line.fulfilmentPct === null ? "—" : formatPct(line.fulfilmentPct);
  const facts: [string, string][] = [];
  if (line.importedInputKg !== undefined) {
    facts.push(["Açığın ithal girdi karşılığı", `${formatKg(line.importedInputKg)} kg`]);
    if (line.importedInputValueUsd !== undefined) facts.push(["İthal girdi kıymeti", `${formatUsd(line.importedInputValueUsd)} USD`]);
  }
  const numbers: [string, number][] = [["Taahhüt", line.commitmentKg], ["Gerçekleşen", line.fulfilledKg], ["Kalan", line.remainingKg]];
  return (
    <Card role="region" aria-label={t} className="min-w-0 gap-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t}</h2>
        <Badge variant="outline" className="font-mono">GTİP {line.hsCode}</Badge>
      </div>
      <p className="text-3xl font-semibold tabular-nums">{pct}</p>
      <Meter label={`${t} gerçekleşme oranı`} pct={line.fulfilmentPct} valueText={pct} />
      <dl className="grid grid-cols-3 gap-3">
        {numbers.map(([k, v]) => <div key={k}><dt className="text-xs text-muted-foreground">{k}</dt><dd className="font-medium tabular-nums">{formatKg(v)} kg</dd></div>)}
      </dl>
      <p className="tabular-nums">Süre sonu {formatDate(line.endDate)} · {line.daysLeft < 0 ? daysLeftText(line.daysLeft) : `Kalan gün ${formatNumber(line.daysLeft)}`}</p>
      {line.forecastText && (
        <Alert role="note" className="border-warning-border bg-warning text-warning-foreground">
          <TriangleAlert aria-hidden="true" />
          <AlertDescription className="text-warning-foreground"><p data-testid="forecast">{line.forecastText}</p></AlertDescription>
        </Alert>
      )}
      {facts.length > 0 && (
        <dl data-testid="input-facts" className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          {facts.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className="tabular-nums">{v}</dd></div>)}
        </dl>
      )}
    </Card>
  );
}

/** Eldeki stok; API null verirse (kalan yok, eşleme ya da reçete yok) yalnız açıklama yazılır. Çubuklar aynı ölçekte, değerler API'den (yalnız tam sayıya yuvarlanır). */
export function StockCoverage({ c }: { c: IpaStockCoverage | null }) {
  if (c === null) return <p className="text-sm text-muted-foreground">Stok karşılama hesaplanamadı: kalan ihracat taahhüdü, GTİP-mamul eşlemesi ya da reçete yok.</p>;
  return (
    <Card role="region" aria-label="Stok karşılama" className="gap-3 text-sm">
      <h2 className="text-base font-semibold">Stok karşılama</h2>
      <BarList items={[{ label: "Mamul karşılığı (kg)", value: Math.round(c.productEquivalentKg) }, { label: "Kalan taahhüt (kg)", value: Math.round(c.remainingCommitmentKg) }]} />
      <p className="text-muted-foreground">Hammadde stoku {formatKg(c.rawMaterialStockKg)} kg · Mamul stoku {formatKg(c.productStockKg)} kg</p>
      <p className="flex flex-wrap items-center gap-2 font-medium">
        {c.covers ? <StatusBadge tone="success">✓ Karşılıyor</StatusBadge> : <StatusBadge tone="warning">⚠ Karşılamıyor</StatusBadge>}
        {c.covers ? "Eldeki stok kalan taahhüdü karşılıyor." : "Eldeki stok kalan taahhüdü karşılamıyor."}
      </p>
    </Card>
  );
}

const traceHref = (no: string) => `${pathOf("E4")}?ref=${encodeURIComponent(no)}`;

/** Bağlı beyannameler (ithalat ve ihracat satırları birlikte). Tablo kartı. */
export function Declarations({ d }: { d: IpaDetailResponse }) {
  const rows = [d.imports, d.exports].flatMap((l) => (l ? l.declarations.map((x) => ({ type: TITLE[l.commitmentType], ...x })) : []));
  return (
    <section aria-label="Bağlı beyannameler">
      <Card className="gap-0 overflow-clip p-0">
        <h2 className="border-b px-4 py-3 text-base font-semibold">Bağlı beyannameler</h2>
        {rows.length === 0 ? <p className="px-4 py-3 text-sm text-muted-foreground">Bu belgeye bağlı beyanname yok.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className={TH}>Taahhüt</TableHead><TableHead className={TH}>Beyanname</TableHead><TableHead className={TH}>Kalem</TableHead><TableHead className={TH}>Tescil tarihi</TableHead><TableHead className={`${TH} text-right`}>Miktar (kg)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.type}|${r.declarationNo}|${r.itemNo}`}>
                  <TableCell className={TD}>{r.type}</TableCell><TableCell className={`${TD} font-mono text-xs`}><a href={traceHref(r.declarationNo)} onClick={(e) => navigate(e, traceHref(r.declarationNo))} className="rounded-sm text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring">{r.declarationNo}</a></TableCell><TableCell className={TD}>{r.itemNo ?? "—"}</TableCell>
                  <TableCell className={TD}>{formatDate(r.registrationDate)}</TableCell><TableCell className={`${TD} text-right tabular-nums`}>{formatKg(r.quantityKg)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </section>
  );
}

/** Belge seçimi: yerel select, etiketli. */
export function IpaPicker({ list, value, onChange }: { list: IpaListResponse; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="ipa-no">DİİB belgesi</Label>
      <NativeSelect id="ipa-no" value={value} onChange={(e) => onChange(e.target.value)}>
        {list.items.map((i) => <option key={i.ipaNo} value={i.ipaNo}>{i.ipaNo} · süre sonu {formatDate(i.endDate)}</option>)}
      </NativeSelect>
    </div>
  );
}

/** İndirme düğmesi yalnız rapor izni olan rolde; yetkiyi API de denetler. */
export const canDownload = (role: Role) => (PERMISSIONS.report as readonly Role[]).includes(role);

/** E5 (spec §6). Hatırlatıcı ve silme yok. */
export function IpaScreen({ role }: { role: Role }) {
  const [list, setList] = useState<IpaListResponse | null>(null);
  const [ipaNo, setIpaNo] = useState("");
  const [detail, setDetail] = useState<IpaDetailResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getIpaList().then((l) => {
      const want = new URLSearchParams(window.location.search).get("ipa"); // Pano ve paylaşılan bağlantı: ?ipa=<no>
      setList(l); setIpaNo(l.items.find((i) => i.ipaNo === want)?.ipaNo ?? l.items[0]?.ipaNo ?? "");
    }).catch((e) => setError(errorText(e)));
  }, []);
  useEffect(() => {
    if (!ipaNo) return;
    setError("");
    let stale = false; // belge değişince eski isteğin geç yanıtı yok sayılır
    getIpaDetail(ipaNo).then((d) => { if (!stale) setDetail(d); }).catch((e) => { if (!stale) setError(errorText(e)); });
    return () => { stale = true; };
  }, [ipaNo]);

  async function download() {
    setBusy(true); setError("");
    try {
      const { blob, fileName } = await getIpaXlsx(ipaNo);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: fileName }).click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }

  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="DİİB Paneli" description={screens.find((x) => x.id === "E5")!.description} />
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {!list && !error && <div aria-busy="true"><CardSkeleton /></div>}
      {list && list.items.length === 0 && <Card><EmptyState icon={FileSearch} title="DİİB dosyası yüklenmemiş">Veri Kaynakları ekranından yükleyin.</EmptyState></Card>}
      {list && list.items.length > 0 && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-2">
            <IpaPicker list={list} value={ipaNo} onChange={(v) => { setIpaNo(v); replaceQuery("ipa", v); }} />
            {detail && detail.ipaNo === ipaNo && <p className="max-w-3xl text-sm text-muted-foreground">{detail.endDateText}</p>}
          </div>
          {canDownload(role) && <Button variant="outline" disabled={busy || !detail || detail.ipaNo !== ipaNo} onClick={() => void download()}>{busy ? "Hazırlanıyor…" : "Gerçekleşme dökümü (Excel)"}</Button>}
        </div>
      )}
      {!detail && ipaNo && !error && <div aria-busy="true" className="grid gap-6 xl:grid-cols-2"><CardSkeleton /><CardSkeleton /></div>}
      {detail && (
        <div className="flex flex-col gap-6 transition-opacity aria-busy:opacity-50" aria-busy={detail.ipaNo !== ipaNo}>
          <div className="grid gap-6 xl:grid-cols-2">
            {detail.imports && <LineCard line={detail.imports} />}
            {detail.exports && <LineCard line={detail.exports} />}
          </div>
          <StockCoverage c={detail.stockCoverage} />
          <Declarations d={detail} />
        </div>
      )}
    </main>
  );
}
